import styled from '@emotion/styled';
import { colors } from 'folio-design-tokens';
import * as React from 'react';

export interface Props {
  /**
   * Checked state of radio button
   */
  checked: boolean;
  /**
   * Called when the checked state changes
   */
  onChange: (checked: boolean) => void;
  /**
   * HTML ID attribute
   */
  id?: string;
  /**
   * HTML class attribute
   */
  className?: string;
  /**
   * HTML name attribute of the input
   */
  name: string;
  /**
   * The disabled state of the input
   */
  disabled?: boolean;
  tabIndex?: number;
}

const Container = styled.span`
  box-sizing: border-box;
  display: inline-block;
  width: 24px;
  height: 24px;
  position: relative;
  vertical-align: middle;
  pointer-events: none;
`;

const Input = styled.input`
  position: absolute;
  left: -1px;
  width: 24px;
  height: 24px;
  opacity: 0;
  margin: 0;
  z-index: 1;
  pointer-events: auto;
  -webkit-tap-highlight-color: transparent;

  :enabled {
    cursor: pointer;
  }

  + [data-graphics] {
    overflow: visible;

    [data-graphics-background] {
      fill: #fff;
      stroke: ${colors.wcagNonText};
      transition: fill 0.2s, stroke 0.2s;

      @media (prefers-contrast: more) {
        stroke: ${colors.black};
      }
    }

    [data-graphics-checkmark] {
      fill: #fff;
    }
  }

  @media (hover) {
    :enabled:hover:checked + [data-graphics] [data-graphics-background] {
      fill: ${colors.blueHover};
      stroke: ${colors.blueHover};
    }
  }

  /* Unchecked */
  & + [data-graphics] [data-graphics-checkmark] {
    transform: scale(0.5);
    transition: transform 0.15s ease-out;
    transform-origin: 50%;
    stroke: none;
  }

  /* Checked */
  :checked + [data-graphics] {
    [data-graphics-checkmark] {
      transform: scale(1);
    }

    [data-graphics-background] {
      fill: ${colors.blue};
      stroke: ${colors.blue};
    }
  }

  /* Disabled */
  :disabled + [data-graphics] [data-graphics-background] {
    fill: ${colors.grayLight};
    stroke: ${colors.grayMedium};
  }

  :disabled + [data-graphics] [data-graphics-checkmark] {
    fill: none;
  }

  :disabled:checked + [data-graphics] [data-graphics-checkmark] {
    fill: ${colors.grayMedium};
  }

  /* Focused */
  &.focus-visible + [data-graphics] [data-graphics-focus] {
    stroke: ${colors.blue};
  }

  /* TODO: :indeterminate */
`;

const Graphic: React.FC = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      aria-hidden="true"
      data-graphics=""
    >
      {/* Border and background */}
      <circle data-graphics-background="" cx={11} cy={12} r={11.5} />
      {/* Checkark */}
      <circle data-graphics-checkmark="" cx={11} cy={12} r={4} />
      {/* Focus */}
      <circle
        data-graphics-focus=""
        cx={11}
        cy={12}
        r={15}
        strokeWidth={2}
        stroke="none"
        fill="none"
      />
    </svg>
  );
};

export const RadioButton: React.FC<Props> = props => {
  const { checked, disabled, id, name, tabIndex, onChange, className } = props;

  const callback = (evt: React.FormEvent<HTMLInputElement>) => {
    onChange(evt.currentTarget.checked);
  };

  return (
    <Container className={className}>
      <Input
        type="radio"
        checked={checked}
        id={id}
        name={name}
        onChange={callback}
        disabled={disabled}
        tabIndex={tabIndex}
      />
      <Graphic />
    </Container>
  );
};
